import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { eosTool } from '@/services/blockchain/eos-tool'
import type { Pair, TokenInfo } from '@/types'

interface PairsState {
  pairs: Pair[]
  coins: TokenInfo[]
  lastPair: Pair | null
  fetchPairs: () => Promise<void>
  setLastPair: (pair: Pair) => void
}

export const usePairsStore = create<PairsState>()(
  persist(
    (set) => ({
      pairs: [],
      coins: [],
      lastPair: null,
      fetchPairs: async () => {
        const [items, itemsMine, itemsPool] = await Promise.all([
          eosTool.getTableRows<any>({ code: 'swap.ttr', scope: 'swap.ttr', table: 'pairs', limit: 1000, json: true }),
          eosTool.getTableRows<any>({ code: 'mine.ttr', scope: 'mine.ttr', table: 'pools', limit: 300, json: true }),
          eosTool.getTableRows<any>({ code: 'pools.ttr', scope: 'pools.ttr', table: 'pools', limit: 300, json: true }),
        ])

        const list: Pair[] = items.map((v: any) => {
          const p: any = { mineWeight: 0, poolWeight: 0 }
          const itemMine = itemsMine.find((x: any) => x.pair_id == v.id)
          if (itemMine) { p.mineWeight = parseFloat(parseFloat(itemMine.weight).toFixed(2)); p.mineInfo = itemsMine }
          const itemPool = itemsPool.find((x: any) => x.pair_id == v.id)
          if (itemPool) { p.poolWeight = parseFloat(parseFloat(itemPool.weight).toFixed(2)); p.poolInfo = itemPool }
          p.id = v.id
          p.totalLiquidity = v.total_liquidity
          const currency0 = v.token0.sym.split(',')[1]
          const precision0 = parseInt(v.token0.sym.split(',')[0])
          const currency1 = v.token1.sym.split(',')[1]
          const precision1 = parseInt(v.token1.sym.split(',')[0])
          p.token0 = {
            coin: currency0, precision: precision0, contract: v.token0.contract,
            reserve: v.reserve0, tokenId: `${v.token0.contract}-${currency0}`,
            eosAmount: currency0 === 'EOS' ? parseFloat(v.reserve0) : 0,
          }
          p.token1 = {
            coin: currency1, precision: precision1, contract: v.token1.contract,
            reserve: v.reserve1, tokenId: `${v.token1.contract}-${currency1}`,
            eosAmount: currency1 === 'EOS' ? parseFloat(v.reserve1) : 0,
          }
          return p
        })

        // Extract unique coins
        const allCoins = [...list.map((p) => p.token0), ...list.map((p) => p.token1)]
        const uniqueCoins: TokenInfo[] = []
        for (const coin of allCoins) {
          if (!uniqueCoins.some((c) => c.tokenId === coin.tokenId)) uniqueCoins.push(coin)
        }
        uniqueCoins.sort((a, b) => b.eosAmount - a.eosAmount)

        set({ pairs: list, coins: uniqueCoins })
      },
      setLastPair: (pair) => set({ lastPair: pair }),
    }),
    {
      name: 'totoro-pairs',
      partialize: (state) => ({ pairs: state.pairs, lastPair: state.lastPair }),
    },
  ),
)
